<template>
    <tbody :id="'order-' + order.uuid">
    <tr class="table-row">
        <td :colspan="_isAdvertiser ? 2 : 1">
            <span class="nowrap text-md">
                <a class="text-black" :href="searchUrl">
                    {{ order.uuid }}
                </a>
            </span>
            <div class="small">{{ order.created_at_formatted }}</div>
            <div class="position-relative subrow">
                <small class="pull-more nowrap">
                    <t-img
                        v-if="order.team"
                        height="30"
                        width="30"
                        class="rounded-circle"
                        :src="order.team.photo_url"
                        alt="Team Logo"
                    ></t-img>
                    {{ order.team ? order.team.name : '' }}
                </small>
            </div>
        </td>
        <td v-if="!_isAdvertiser">
            {{ order.advertiser ? order.advertiser.name : '' }}
        </td>
        <td>
            {{ order.assets_count }}
        </td>
        <td>
            <run-dates :start="order.start_date" :end="order.end_date"></run-dates>
        </td>
        <td>
            {{ order.units_count }}
        </td>
        <td>
            {{ order.amount_formatted }}
        </td>
        <td v-if="_isAdmin">
                <span v-if="order.last_transaction">
                    {{ order.last_transaction.status_label }}
                </span>
        </td>
        <td colspan="2">
            <order-row-actions
                v-if="!disableActions"
                :order="order"
                :user-role="_userType"
                @open-review="handleOpenReview"
                @open-view-creative="handleViewCreative"
                @update-item-status="handleUpdateItemStatus"
                @update-order-status="handleUpdateOrderStatus"
            />
        </td>
    </tr>

    <!-- Order Assets -->
    <template v-if="order.order_assets && order.order_assets.length">
        <template v-for="(orderAsset, assetIndex) in sortedOrderAssets">
            <!--                <tr :key="'spacer-start-' + orderAsset.id" class="row-spacer table-sub-row">-->
            <!--                    <td colspan="2"></td>-->
            <!--                    <td colspan="5" class="level-row-start"></td>-->
            <!--                    <td class="cell-spacer"></td>-->
            <!--                </tr>-->

            <!-- Non-bundle asset -->
            <template v-if="!orderAsset.is_bundle">
                <orders-table-order-asset
                    :key="'asset-' + orderAsset.id"
                    :order="order"
                    :order-asset="orderAsset"
                    :user-role="_userType"
                    :disable-actions="disableActions"
                    :toggle-key="getAssetToggleKey(orderAsset)"
                    :expanded-keys="expandedKeys"
                    @toggle="toggleExpanded"
                    @open-review="handleOpenReview"
                    @open-view-creative="handleViewCreative"
                    @update-item-status="handleUpdateItemStatus"
                />

                <!-- Order Items for non-bundle -->
                <template v-if="isExpanded(getAssetToggleKey(orderAsset))">
                    <template v-for="orderItem in orderAsset.order_items">
                        <orders-table-order-item
                            :key="'item-' + orderItem.id"
                            :order="order"
                            :order-item="orderItem"
                            :order-asset="orderAsset"
                            :user-role="_userType"
                            :disable-actions="disableActions"
                            :toggle-key="getItemToggleKey(orderAsset, orderItem)"
                            :expanded-keys="expandedKeys"
                            @toggle="toggleExpanded"
                            @open-review="handleOpenReview"
                            @open-view-creative="handleViewCreative"
                        />

                        <!-- Creative for order item -->
                        <template v-if="isExpanded(getItemToggleKey(orderAsset, orderItem))">
                            <orders-table-order-creative
                                v-for="(creative, creativeIndex) in orderItem.creative"
                                :key="'creative-' + creative.id"
                                :creative="creative"
                                :creative-index="creativeIndex"
                                :order="order"
                                :order-item="orderItem"
                                :order-asset="orderAsset"
                                :user-role="_userType"
                                :disable-actions="disableActions"
                                @open-review="handleOpenReview"
                                @open-view-creative="handleViewCreative"
                            />
                        </template>
                    </template>
                </template>
            </template>

            <!-- Bundle asset -->
            <template v-else>
                <orders-table-order-asset
                    :key="'bundle-' + orderAsset.id"
                    :order="order"
                    :order-asset="orderAsset"
                    :user-role="_userType"
                    :disable-actions="disableActions"
                    :toggle-key="getAssetToggleKey(orderAsset)"
                    :expanded-keys="expandedKeys"
                    @toggle="toggleExpanded"
                    @open-review="handleOpenReview"
                    @open-view-creative="handleViewCreative"
                    @update-item-status="handleUpdateItemStatus"
                />

                <!-- Child assets in bundle -->
                <template v-if="isExpanded(getAssetToggleKey(orderAsset))">
                    <template v-for="childAsset in orderAsset.children">
                        <orders-table-order-asset
                            :key="'child-' + childAsset.id"
                            :order="order"
                            :order-asset="childAsset"
                            :parent-asset="orderAsset"
                            :user-role="_userType"
                            :disable-actions="disableActions"
                            :toggle-key="getChildAssetToggleKey(orderAsset, childAsset)"
                            :expanded-keys="expandedKeys"
                            @toggle="toggleExpanded"
                            @open-review="handleOpenReview"
                            @open-view-creative="handleViewCreative"
                            @update-item-status="handleUpdateItemStatus"
                        />

                        <!-- Order Items for child asset -->
                        <template v-if="isExpanded(getChildAssetToggleKey(orderAsset, childAsset))">
                            <template v-for="orderItem in childAsset.order_items">
                                <orders-table-order-item
                                    :key="'child-item-' + orderItem.id"
                                    :order="order"
                                    :order-item="orderItem"
                                    :order-asset="childAsset"
                                    :parent-asset="orderAsset"
                                    :user-role="_userType"
                                    :disable-actions="disableActions"
                                    :toggle-key="getChildItemToggleKey(orderAsset, childAsset, orderItem)"
                                    :expanded-keys="expandedKeys"
                                    @toggle="toggleExpanded"
                                    @open-review="handleOpenReview"
                                    @open-view-creative="handleViewCreative"
                                />

                                <!-- Creative for child order item -->
                                <template v-if="isExpanded(getChildItemToggleKey(orderAsset, childAsset, orderItem))">
                                    <orders-table-order-creative
                                        v-for="(creative, creativeIndex) in orderItem.creative"
                                        :key="'child-creative-' + creative.id"
                                        :creative="creative"
                                        :creative-index="creativeIndex"
                                        :order="order"
                                        :order-item="orderItem"
                                        :order-asset="childAsset"
                                        :user-role="_userType"
                                        :disable-actions="disableActions"
                                        @open-review="handleOpenReview"
                                        @open-view-creative="handleViewCreative"
                                    />
                                </template>
                            </template>
                        </template>
                    </template>
                </template>
            </template>

            <!--                <tr :key="'spacer-end-' + orderAsset.id" class="row-spacer table-sub-row">-->
            <!--                    <td colspan="2"></td>-->
            <!--                    <td colspan="5" class="level-row-end"></td>-->
            <!--                    <td class="cell-spacer"></td>-->
            <!--                </tr>-->
        </template>
    </template>

    <!-- Empty state -->
    <tr v-else class="table-sub-row">
        <td class="cell-spacer"></td>
        <td colspan="7" style="height: 50px"></td>
    </tr>

    <!-- Bottom actions row -->
    <tr class="table-sub-row table-last-row">
        <td colspan="9">
            <div class="ml-2">
                <order-status-actions
                    v-if="!disableActions && !deniedOnly"
                    :order="order"
                    @update-order-status="handleUpdateOrderStatus"
                />
            </div>
        </td>
    </tr>

    <tr class="row-spacer">
        <td colspan="8"></td>
    </tr>
    </tbody>
</template>

<script>
import OrdersTableApi from '@js/services/OrdersTableApi';
import OrderRowActions from './partials/OrderRowActions.vue';
import OrderStatusActions from './partials/OrderStatusActions.vue';
import OrdersTableOrderAsset from './OrdersTableOrderAsset.vue';
import OrdersTableOrderItem from './OrdersTableOrderItem.vue';
import OrdersTableOrderCreative from './OrdersTableOrderCreative.vue';

export default {
    name: 'OrdersTableOrder',

    components: {
        OrderRowActions,
        OrderStatusActions,
        OrdersTableOrderAsset,
        OrdersTableOrderItem,
        OrdersTableOrderCreative,
    },

    props: {
        order: {
            type: Object,
            required: true,
        },
        disableActions: {
            type: Boolean,
            default: false,
        },
        deniedOnly: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            expandedKeys: [],
            updating: false,
        };
    },

    computed: {
        searchUrl() {
            const base = this._isAdmin
                ? '/admin/orders/search'
                : this._isAdvertiser
                    ? '/advertiser/orders/search'
                    : '/team/orders/search';
            return `${base}?date=&search=${this.order.uuid}`;
        },

        sortedOrderAssets() {
            if (!this.order.order_assets) return [];
            return [...this.order.order_assets].sort((a, b) =>
                (a.name || '').localeCompare(b.name || ''),
            );
        },
    },

    methods: {
        toggleExpanded(key) {
            const idx = this.expandedKeys.indexOf(key);
            if (idx > -1) {
                this.expandedKeys.splice(idx, 1);
            } else {
                this.expandedKeys.push(key);
            }
        },

        isExpanded(key) {
            return this.expandedKeys.includes(key);
        },

        getAssetToggleKey(orderAsset) {
            return `Order${this.order.id}OrderAsset${orderAsset.id}`;
        },

        getChildAssetToggleKey(parentAsset, childAsset) {
            return `Order${this.order.id}OrderAsset${parentAsset.id}ChildAsset${childAsset.id}`;
        },

        getItemToggleKey(orderAsset, orderItem) {
            return `Order${this.order.id}OrderAsset${orderAsset.id}OrderItem${orderItem.id}`;
        },

        getChildItemToggleKey(parentAsset, childAsset, orderItem) {
            return `Order${this.order.id}OrderAsset${parentAsset.id}ChildAsset${childAsset.id}OrderItem${orderItem.id}`;
        },

        handleOpenReview(creativeIds, orderId, isOrder = false) {
            this.$emit('open-review', creativeIds, orderId, isOrder);
        },

        handleViewCreative(creativeIds, orderId, isOrder = false) {
            this.$emit('open-view-creative', creativeIds, orderId, isOrder);
        },

        async handleUpdateItemStatus(orderItemIds, status) {
            if (this.updating) return;

            this.updating = true;

            try {
                await OrdersTableApi.updateOrderItemStatus(this.order.id, {
                    ids: orderItemIds,
                    status: status,
                });

                this.$emit('refresh');
            } catch (error) {
                console.error('Failed to update order item status:', error);
            } finally {
                this.updating = false;
            }
        },

        async handleUpdateOrderStatus(status) {
            if (this.updating) return;

            this.updating = true;

            try {
                await OrdersTableApi.updateOrderStatus(this.order.id, {
                    status: status,
                });

                this.$emit('refresh');
            } catch (error) {
                console.error('Failed to update order status:', error);
            } finally {
                this.updating = false;
            }
        },
    },
};
</script>
